import { useState, useEffect, useRef } from 'react';

const PROGRESS_STEPS = {
  feasibility: { step: '🔍 Checking idea feasibility...', percentage: 5 },
  traditional: [
    { step: 'Analyzing your idea...', percentage: 20 },
    { step: 'Generating technical requirements...', percentage: 40 },
    { step: 'Researching market analysis...', percentage: 60 },
    { step: 'Analyzing performance metrics...', percentage: 80 },
    { step: 'Finalizing report...', percentage: 95 }
  ],
  strands_standard: [
    { step: '🤖 Multi-Agent: Technical requirements analysis...', percentage: 15 },
    { step: '📊 Market analysis & competition research...', percentage: 30 },
    { step: '⚡ Performance metrics & use cases...', percentage: 50 },
    { step: '📄 Research papers & academic landscape...', percentage: 70 },
    { step: '📋 Idea summary & insights compilation...', percentage: 85 },
    { step: 'Finalizing report...', percentage: 95 }
  ],
  strands_advanced: [
    { step: '🤖 Multi-Agent: Technical requirements analysis...', percentage: 10 },
    { step: '📊 Market analysis & competition research...', percentage: 20 },
    { step: '⚡ Performance metrics & use cases...', percentage: 30 },
    { step: '📄 Research papers & academic landscape...', percentage: 40 },
    { step: '📋 Idea summary & insights compilation...', percentage: 50 },
    { step: '⚖️ Regulatory compliance & standards...', percentage: 60 },
    { step: '💰 Financial analysis & projections...', percentage: 70 },
    { step: '⚠️ Risk assessment & mitigation...', percentage: 80 },
    { step: '🏆 Competitor analysis & market position...', percentage: 90 },
    { step: 'Finalizing report...', percentage: 95 }
  ]
};

export const useProgressTracker = (backend, isActive, advancedMode = false) => {
  const [progress, setProgress] = useState({ step: '', percentage: 0 });
  const intervalRef = useRef(null);
  const currentStepIndexRef = useRef(0);

  useEffect(() => {
    if (isActive) {
      // Start with feasibility check
      setProgress(PROGRESS_STEPS.feasibility);
      currentStepIndexRef.current = 0;

      // Wait a moment before moving to main steps
      // (feasibility will call markFeasibilityComplete when done)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setProgress({ step: '', percentage: 0 });
      currentStepIndexRef.current = 0;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [backend, isActive, advancedMode]);

  const markFeasibilityComplete = () => {
    
    // Select the appropriate steps based on backend and mode
    let steps;
    if (backend === 'strands') {
      steps = advancedMode ? PROGRESS_STEPS.strands_advanced : PROGRESS_STEPS.strands_standard;
    } else {
      steps = PROGRESS_STEPS.traditional;
    }

    // Start progressing through main steps
    intervalRef.current = setInterval(() => {
      if (currentStepIndexRef.current < steps.length) {
        setProgress(steps[currentStepIndexRef.current]);
        currentStepIndexRef.current++;
      } else {
        clearInterval(intervalRef.current);
      }
    }, 2000);
  };

  const complete = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setProgress({ step: 'Complete!', percentage: 100 });
  };

  const reset = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setProgress({ step: '', percentage: 0 });
    currentStepIndexRef.current = 0;
  };

  return { progress, complete, reset, markFeasibilityComplete };
};
